/**
 * What the Overview does when a link lands on it (twin slice 1, plan W4
 * step 10; catalogue §8 "Landing"), as pure rules the screen applies.
 *
 * LANDING IDENTITY. A landing is the initial load with locate parameters,
 * or a PUSH navigation carrying `state.nxLanding = <id>` -- which is how
 * every producer goes there ("Show where" on a toast, a bell row or a
 * drawer: `show-where.ts` mints a fresh id per press). Moves inside the
 * view use `replace` and carry no new id, so they never count: react-router
 * mints a new `location.key` on every replace, which is why a landing is
 * NEVER keyed on `location.key`. The screen keeps the last id it handled in
 * a ref and compares against it.
 *
 * WHAT A LANDING DOES, ONCE:
 *   - the floor: where the resolver says the object is, the moment it has
 *     an answer (`settledFloor`); after that, nothing the live data does
 *     moves the floor or the camera -- not the robot riding to another
 *     floor, not the answer changing;
 *   - the drawer: a landing for a DIFFERENT subject closes the open one;
 *     the same subject leaves it open (`landingClosesDrawer`). A link never
 *     opens one;
 *   - the callout (`calloutModel`): what the link pointed at, where it is,
 *     and an "Open <name> panel" button -- a tap the person makes, like a
 *     tap on the canvas. It never says a card opened.
 *
 * NEVER INVENT A FLOOR. A lift's car is placed only where its own row
 * says, and only while that reading can be trusted (`LiftLook.level`). A
 * landing that would take a lost car's last floor lands on the whole
 * building instead, and the callout says the car's floor is not known.
 * The callout names a floor only from the object's own reading, never
 * from the floor the view landed on: that may be the link's hint, or the
 * floor of the robot a lift link followed. A stale position is said in
 * the past tense.
 *
 * Every string here is copy for review (plan gate B).
 */
import { levelWords } from '../../state/attention/words';
import type { LiftLanding } from '../../state/lift-landing';
import type { LocateFocus } from '../../state/locate';
import { hasLocateParams } from '../../state/locate';
import {
  focusName,
  type LocateFound,
  type LocateResolution,
  type LocateTarget,
} from '../../state/locate-resolve';
import { landingIdOf } from '../../state/show-where';
import { sameLevel } from '../../utils/levels';
import type { MapSelectionKind, MapSelectionSubject } from '../Map/MapSelectionHost';
import type { RobotPresence } from '../Map/robot-presence';

// ---------------------------------------------------------------------------
// Landing identity
// ---------------------------------------------------------------------------

/** The landing id of an initial load whose URL carries locate parameters
 *  but whose history entry carries no id of its own. */
export const INITIAL_LANDING_ID = 'initial';

export type NavigationKind = 'PUSH' | 'POP' | 'REPLACE';

export interface LandingSource {
  /** This is the screen's first look at the location since it mounted. */
  readonly firstLook: boolean;
  /** react-router's `useNavigationType()`. */
  readonly navigationType: NavigationKind;
  readonly search: string;
  /** `location.state`. */
  readonly state: unknown;
}

/**
 * The landing this location asks for, or null when it asks for none.
 *
 *   - the first look, with a pushed id: that id;
 *   - the first look, with locate parameters: `INITIAL_LANDING_ID`;
 *   - after that, only a PUSH carrying an id. A POP (Back, Forward) or a
 *     REPLACE (every move inside the view) is never a landing.
 *
 * The caller compares the answer with the last id it handled.
 */
export function landingIdFor(src: LandingSource): string | null {
  const pushed = landingIdOf(src.state);
  if (src.firstLook) return pushed ?? (hasLocateParams(src.search) ? INITIAL_LANDING_ID : null);
  return src.navigationType === 'PUSH' ? pushed : null;
}

// ---------------------------------------------------------------------------
// The subject, and the open drawer
// ---------------------------------------------------------------------------

/** The host subject a resolved target is: a robot by its BARE name, as the
 *  host keys one; null for a floor, which has no drawer. */
export function subjectOfTarget(target: LocateTarget | null): MapSelectionSubject | null {
  if (!target) return null;
  switch (target.kind) {
    case 'robot':
      return { kind: 'robot', id: target.robotName };
    case 'lift':
      return { kind: 'lift', id: target.liftName };
    case 'door':
      return { kind: 'door', id: target.doorName };
    case 'zone':
      return { kind: 'zone', id: target.place };
    case 'level':
      return null;
  }
}

/** What a landing is about: the resolver's target when it found one, the
 *  link's own focus otherwise (an alert or a job names no drawer). */
export function landingSubject(
  focus: LocateFocus | null,
  resolution: LocateResolution | null,
): MapSelectionSubject | null {
  if (resolution?.status === 'found' && resolution.target) {
    return subjectOfTarget(resolution.target);
  }
  if (!focus) return null;
  const kinds: Partial<Record<LocateFocus['kind'], MapSelectionKind>> = {
    robot: 'robot',
    lift: 'lift',
    door: 'door',
    zone: 'zone',
  };
  const kind = kinds[focus.kind];
  if (!kind) return null;
  const id =
    focus.kind === 'robot'
      ? focus.robotName
      : focus.kind === 'lift'
        ? focus.liftName
        : focus.kind === 'door'
          ? focus.doorName
          : focus.kind === 'zone'
            ? focus.place
            : '';
  return { kind, id };
}

export function sameSubject(
  a: MapSelectionSubject | null | undefined,
  b: MapSelectionSubject | null | undefined,
): boolean {
  return !!a && !!b && a.kind === b.kind && a.id === b.id;
}

/** A landing closes the open drawer unless it is about the same subject. */
export function landingClosesDrawer(
  open: MapSelectionSubject | null,
  subject: MapSelectionSubject | null,
): boolean {
  return open !== null && !sameSubject(open, subject);
}

// ---------------------------------------------------------------------------
// The floor a landing settles on
// ---------------------------------------------------------------------------

/**
 * How a robot's floor may be said: `now` (reporting), `once` (seen once,
 * not yet vouched for: W3's `checking`), `last-seen` (lost: its ghost's
 * floor) or `last-placed` (reporting, but it lost its place on the map:
 * the floor of its newest real pose, which a lift ride may have left
 * behind).
 */
export type RobotFloorWhen = 'now' | 'once' | 'last-seen' | 'last-placed';

/** Where the view draws a robot: its mark, its ghost or its "?" row. */
export interface RobotFloorFact {
  readonly level: string;
  readonly when: RobotFloorWhen;
}

/** The robot a link to a lift followed to the robot's OWN floor. */
export interface LiftRobotFact {
  /** Its bare name, as the callout says it. */
  readonly name: string;
  /** The floor the landing went to. */
  readonly level: string;
  /** How that floor may be said; null when presence has nothing on it. */
  readonly when: RobotFloorWhen | null;
}

export interface LandingFacts {
  /** The floor a car reports through its look; null while it is lost or
   *  unreported (`LiftLook.level`). Never the raw row's `currentLevel`. */
  carLevel(liftName: string): string | null;
  /** The robot a link to this lift lands on, on its OWN floor
   *  (`liftLandingLevel`'s `robot-floor`), which no car reading decides;
   *  null when the landing followed no robot, or one inside the car. */
  liftRobot(liftName: string): LiftRobotFact | null;
  /** Where the view draws this robot (`robotFloorFact`); null when it
   *  draws it nowhere. */
  robotFloor(fleetName: string, robotName: string): RobotFloorFact | null;
}

/**
 * Where the view draws a robot, from W3's presence: its live pose's floor
 * (`now`, or `once` while it is checking), its ghost's when it is lost
 * (`last-seen`), and its "?" row's when it reports but cannot be placed
 * (`last-placed`). Null when nothing of it is drawn -- a lost robot with
 * no pose on record, a rider (its car is drawn), or a robot presence has
 * not classified yet -- which is when a callout names no floor for it.
 * Null too for a lost robot whose ghost stands inside a car (presence's
 * `liftName`): that pose names the floor it BOARDED on, so it is no floor
 * of the robot's; it is said "last seen in SL1", never "on Level 9".
 */
export function robotFloorFact(presence: RobotPresence | undefined): RobotFloorFact | null {
  if (!presence) return null;
  const map = presence.pose?.map;
  if (map) {
    if (presence.kind === 'lost' && presence.liftName) return null;
    if (presence.kind === 'lost') return { level: map, when: 'last-seen' };
    return { level: map, when: presence.checking ? 'once' : 'now' };
  }
  if (presence.kind === 'unknown' && presence.level) {
    return { level: presence.level, when: 'last-placed' };
  }
  return null;
}

/** The robot `landing` followed to its own floor, or null. */
export function liftRobotFact(
  landing: LiftLanding,
  presence: ReadonlyMap<string, RobotPresence>,
): LiftRobotFact | null {
  if (landing.basis !== 'robot-floor' || !landing.level || !landing.robotKey) return null;
  const key = landing.robotKey;
  const drawn = robotFloorFact(presence.get(key));
  return {
    name: key.slice(key.indexOf('/') + 1),
    level: landing.level,
    when: drawn && sameLevel(drawn.level, landing.level) ? drawn.when : null,
  };
}

/**
 * The floor a landing moves to once the resolver has an answer: the found
 * object's floor (null: the whole building), or the link's hint when it
 * could not be found. A car whose floor cannot be read is never given one:
 * a ring on such a car (a rider's, or the lift's own) lands on the whole
 * building, unless the lift landing followed a robot on its own floor.
 * Null while the resolver is still finding.
 */
export function settledFloor(
  resolution: LocateResolution,
  facts: LandingFacts,
): { readonly level: string | null } | null {
  if (resolution.status === 'finding') return null;
  if (resolution.status === 'not-found') return { level: resolution.level };
  const ring = resolution.ring;
  if (ring?.kind === 'lift' && facts.carLevel(ring.liftName) === null) {
    const byRobot = resolution.target?.kind === 'lift' && facts.liftRobot(ring.liftName) !== null;
    return { level: byRobot ? resolution.level : null };
  }
  return { level: resolution.level };
}

// ---------------------------------------------------------------------------
// The callout
// ---------------------------------------------------------------------------

export interface CalloutOpen {
  readonly kind: 'robot' | 'lift' | 'door';
  /** What the host's handler takes: a robot's BARE name. */
  readonly id: string;
  readonly label: string;
}

export interface CalloutModel {
  readonly title: string;
  readonly lines: readonly string[];
  /** The "Open <name> panel" button, for a robot, lift or door. */
  readonly open: CalloutOpen | null;
}

export const openPanelLabel = (name: string) => `Open ${name} panel`;
const floorWords = (level: string) => levelWords(level) ?? level;
export const onFloorText = (level: string) => `On ${floorWords(level)}.`;
/** A robot seen once: its floor, said as one report, not as a fact. */
export const onFloorOneReportText = (level: string) =>
  `On ${floorWords(level)}, from one report so far.`;
/** A lost robot's ghost floor, after the resolver's "Not reporting. Last
 *  seen 13:50.": past tense, never "On Level 7." */
export const wasOnFloorText = (level: string) => `It was on ${floorWords(level)}.`;
/** An unplaced robot's "?" row floor, after "Lost its place on the map." */
export const lastPlacedText = (level: string) => `Last placed on ${floorWords(level)}.`;
/** The robot a lift link followed to its own floor, in the tense its
 *  presence allows; no floor when presence has nothing on it. */
export function liftInUseText(robot: LiftRobotFact): string {
  const floor = floorWords(robot.level);
  switch (robot.when) {
    case 'now':
      return `In use by ${robot.name}, which is on ${floor}.`;
    case 'once':
      return `In use by ${robot.name}, on ${floor} from one report so far.`;
    case 'last-seen':
      return `In use by ${robot.name}, last seen on ${floor}.`;
    case 'last-placed':
      return `In use by ${robot.name}, last placed on ${floor}.`;
    case null:
      return `In use by ${robot.name}.`;
  }
}
/** A place the map no longer has, landed on the floor its name gives. */
export const floorFromNameText = (level: string) => `Its name puts it on ${floorWords(level)}.`;
export const viewShowsText = (level: string) => `This view shows ${floorWords(level)}.`;
export const carFloorUnknownText = (lift: string) =>
  `${lift} isn't reporting which floor its car is on.`;
export const TEST_ROBOT_TEXT = 'This is a test robot.';
/** A link whose `focus=` this console cannot read (hand-edited, cut short,
 *  or a robot without its fleet): said, never silently dropped. */
export const UNREADABLE_LINK: CalloutModel = Object.freeze({
  title: 'This link',
  lines: Object.freeze(["This link doesn't say what to show."]),
  open: null,
});
export const ALERT_TITLE = 'This alert';
export const TASK_TITLE = 'This job';

function targetName(target: LocateTarget): string {
  switch (target.kind) {
    case 'robot':
      return target.robotName;
    case 'lift':
      return target.liftName;
    case 'door':
      return target.doorName;
    case 'zone':
      return target.place;
    case 'level':
      return levelWords(target.levelName) ?? target.levelName;
  }
}

function focusTitle(focus: LocateFocus): string {
  if (focus.kind === 'alert') return ALERT_TITLE;
  if (focus.kind === 'task') return TASK_TITLE;
  return focusName(focus);
}

function openFor(target: LocateTarget | null): CalloutOpen | null {
  if (!target) return null;
  switch (target.kind) {
    case 'robot':
      return { kind: 'robot', id: target.robotName, label: openPanelLabel(target.robotName) };
    case 'lift':
      return { kind: 'lift', id: target.liftName, label: openPanelLabel(target.liftName) };
    case 'door':
      return { kind: 'door', id: target.doorName, label: openPanelLabel(target.doorName) };
    default:
      return null;
  }
}

export interface CalloutInputs {
  readonly focus: LocateFocus;
  readonly resolution: LocateResolution;
  /** The floor the view shows, or null for the whole building. */
  readonly viewFloor: string | null;
  readonly facts: LandingFacts;
}

/**
 * What the callout says. While finding, and when not found, it is the
 * resolver's one line. Once found: the object's name, where it is, the
 * floor the view shows when that is another, the resolver's own notes
 * ("Inside SL1.", "Not reporting. Last seen 14:02.", "Cleared at 10:55.
 * Showing SL1 as it is now."), and a test robot. It names what was found
 * and where; it never says a card or a panel opened.
 *
 * WHERE IT IS comes only from the object's own reading, never from the
 * floor the view landed on (`resolution.level`), which may be a hint or
 * another object's floor:
 *   - a lift: its CAR's floor (`carLevel`), or that the car isn't
 *     reporting one -- never the floor of the robot the landing followed,
 *     which gets a line of its own ("In use by phantas_01, which is on
 *     Level 9.");
 *   - a rider: its car's floor, the same way. A robot LAST SEEN in a car
 *     (a silent rider's ghost) gets no floor: the resolver's line names
 *     the car ("Not reporting. Last seen in SL1 at 14:52.");
 *   - a robot: where the view draws it (`robotFloor`): "On Level 7." live,
 *     "On Level 7, from one report so far." when seen once, and in the
 *     past tense after the resolver's own line when the position is old:
 *     a lost robot's ghost ("It was on Level 7.") and an unplaced robot's
 *     "?" row ("Last placed on Level 7."). A robot drawn nowhere, or on a
 *     floor other than the one it landed on, gets no floor;
 *   - a door or a place: its registered floor;
 *   - nothing placeable (an ambiguous name, a gone or forbidden record, an
 *     alert about no one place): no floor at all -- the view's floor is
 *     the link's hint, not a finding. A renamed place says its floor comes
 *     from its name.
 */
export function calloutModel({ focus, resolution, viewFloor, facts }: CalloutInputs): CalloutModel {
  if (resolution.status !== 'found') {
    return { title: focusTitle(focus), lines: [resolution.text], open: null };
  }
  const found: LocateFound = resolution;
  const target = found.target;
  const ring = found.ring;
  /** Lines ahead of the resolver's notes, and behind them. */
  const before: string[] = [];
  const after: string[] = [];
  const inView = (line: string, floor: string) =>
    viewFloor && !sameLevel(viewFloor, floor) ? `${line} ${viewShowsText(viewFloor)}` : line;

  if (!target) {
    // After the resolver's "L09-GLL isn't on the current map; ...".
    if (ring?.kind === 'level')
      after.push(inView(floorFromNameText(ring.levelName), ring.levelName));
  } else if (target.kind === 'lift') {
    const car = facts.carLevel(target.liftName);
    before.push(car ? inView(onFloorText(car), car) : carFloorUnknownText(target.liftName));
    const robot = facts.liftRobot(target.liftName);
    if (robot) before.push(liftInUseText(robot));
  } else if (target.kind === 'robot' && ring?.kind === 'lift') {
    // A rider: its car's floor. A robot last seen in a car (a silent
    // rider's ghost, `not-reporting`): no floor at all -- where the car is
    // now is not where a robot nobody hears is, and its pose names the
    // floor it boarded on. The resolver's line names the car.
    if (found.reason === 'in-lift') {
      const car = facts.carLevel(ring.liftName);
      if (car) before.push(inView(onFloorText(car), car));
      else after.push(carFloorUnknownText(ring.liftName));
    }
  } else if (target.kind === 'robot') {
    // Only where the view draws it, and only when that is where it
    // landed: a floor from the link's hint is never said as its own.
    const drawn = facts.robotFloor(target.fleetName, target.robotName);
    const floor = drawn && found.level && sameLevel(drawn.level, found.level) ? found.level : null;
    if (drawn && floor) {
      if (drawn.when === 'now') before.push(inView(onFloorText(floor), floor));
      else if (drawn.when === 'once') before.push(inView(onFloorOneReportText(floor), floor));
      else if (drawn.when === 'last-seen') after.push(inView(wasOnFloorText(floor), floor));
      else after.push(inView(lastPlacedText(floor), floor));
    }
  } else if (target.kind !== 'level' && found.level) {
    before.push(inView(onFloorText(found.level), found.level));
  }

  const lines = [...before, ...found.notes, ...after];
  if (found.simulated) lines.push(TEST_ROBOT_TEXT);
  return {
    title: target ? targetName(target) : focusTitle(focus),
    lines,
    open: openFor(target),
  };
}

// ---------------------------------------------------------------------------
// The one highlight
// ---------------------------------------------------------------------------

/** `{kind, id}` as both renderers' highlight prop spells it. */
export interface OverviewHighlight {
  readonly kind: string;
  readonly id: string;
}

/** The ring the resolver asks for: a robot fleet-qualified, a rider's on
 *  its car. Null until something is found. */
export function focusHighlight(resolution: LocateResolution | null): OverviewHighlight | null {
  if (resolution?.status !== 'found' || !resolution.ring) return null;
  const ring = resolution.ring;
  switch (ring.kind) {
    case 'robot':
      return { kind: 'robot', id: `${ring.fleetName}/${ring.robotName}` };
    case 'lift':
      return { kind: 'lift', id: ring.liftName };
    case 'door':
      return { kind: 'door', id: ring.doorName };
    case 'zone':
      return { kind: 'zone', id: ring.place };
    case 'level':
      return { kind: 'level', id: ring.levelName };
  }
}

/**
 * What the view rings: the open drawer's subject when one is open about
 * something else (the person's latest tap), the link's ring otherwise, and
 * the drawer's subject when there is no link. The link's ring wins for its
 * own subject because it knows more -- a rider is rung on its car.
 */
export function chooseHighlight(args: {
  readonly focusRing: OverviewHighlight | null;
  readonly focusSubject: MapSelectionSubject | null;
  readonly selected: MapSelectionSubject | null;
  readonly selectedRing: OverviewHighlight | null;
}): OverviewHighlight | null {
  const { focusRing, focusSubject, selected, selectedRing } = args;
  if (selected && !sameSubject(selected, focusSubject)) return selectedRing;
  return focusRing ?? selectedRing;
}
